import { memo, useState } from "react"
import { Form, useFormikContext } from "formik"
import { Formik } from "../../../../../../shared/components/forms/SchemaFormik"
import * as yup from "yup"
import { Box, IconButton, MenuItem, Popover, Tooltip, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo, faClose } from "@fortawesome/pro-light-svg-icons"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { LocalizedEnum } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { StandardLoadingButton } from "../../../../../../shared/components/buttons/LoadingButton"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { FormikDropdown, FormikNumberField, FormikTextArea, FormikTextField } from "../../../../../../shared/components/forms/Formik"
import { iterateStringEnum } from "../../../../../../lib/enums"
import {
    ProductStockInfo,
    StockAdjustmentType,
    StockChangeReason,
    StockPopoverFormValues,
    CombinedStockFormValues,
    UpdateStockPayload,
    INBOUND_REASONS,
    OUTBOUND_REASONS,
    ADJUSTMENT_TO_TRANSACTION,
} from "../../productsTypes"

const useStyles = makeStyles()((theme) => ({
    content: {
        padding: "2rem",
        display: "flex",
        flexDirection: "column",
        gap: "3rem",
        width: "28rem",
    },
    toggleContainer: {
        borderRadius: "5.625rem",
        backgroundColor: theme.palette.tailwind.slate[100],
    },
    toggleGroup: {
        gap: "0.25rem",
        "& .MuiToggleButtonGroup-grouped:not(:last-of-type), & .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
            borderRadius: "5.625rem",
        },
    },
    toggleButton: {
        textTransform: "none",
        border: "none",
        padding: "0.1rem 2rem",
        fontWeight: 400,
        fontSize: "1rem",
        whiteSpace: "nowrap",
        color: theme.palette.text.secondary,
        background: "transparent",
        "&.Mui-selected, &.Mui-selected:hover": {
            fontWeight: 500,
            backgroundColor: "#CBD5E1",
            color: theme.palette.text.primary,
        },
        "&:hover": {
            background: "transparent",
        },
    },
    fieldGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
    },
    labelRow: {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
    },
    inputField: {
        maxWidth: "10rem",
        "& .MuiInputBase-root": {
            height: "2.5rem",
        },
    },
    inputFieldSmall: {
        maxWidth: "6rem",
        "& .MuiInputBase-root": {
            height: "2.5rem",
        },
    },
    reasonField: {
        width: "100%",
        height: "2.5rem",
    },
    noteField: {
        "& .MuiInputBase-root": {
            padding: "8.5px 14px",
        },
    },
    infoIcon: {
        fontSize: "1rem",
        color: theme.palette.text.secondary,
    },
    clearButton: {
        position: "absolute",
        right: "2rem",
        padding: "0.25rem",
        fontSize: "1rem",
    },
    confirmButton: {
        paddingLeft: "2rem",
        paddingRight: "2rem",
    },
}))

type StockTab = "inventory" | "daily"

type StockPopoverProps = Readonly<{
    anchorEl: HTMLElement | null
    onClose: () => void
    productId: number
    currentStock: ProductStockInfo
    onStockUpdate: (payload: UpdateStockPayload) => void
    formMode?: boolean
    disableOutbound?: boolean
    initialInventoryValues?: StockPopoverFormValues
    onValuesChange?: (values: CombinedStockFormValues) => void
    onFormCancel?: () => void
    showValidationErrors?: boolean
}>

export const StockPopover = memo(function StockPopover({
    anchorEl,
    onClose,
    productId,
    currentStock,
    onStockUpdate,
    formMode,
    disableOutbound,
    initialInventoryValues,
    onValuesChange,
    onFormCancel,
    showValidationErrors,
}: StockPopoverProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const [activeTab, setActiveTab] = useState<StockTab>("inventory")

    const handleTabChange = (_event: React.MouseEvent<HTMLElement>, newValue: StockTab | null) => {
        if (newValue !== null) {
            setActiveTab(newValue)
        }
    }

    const currentStockTotal = currentStock.inventory.total

    const validationSchema = yup.object({
        type: yup.string().required(l10n.getString("required-field")),
        quantityDelta: yup
            .number()
            .nullable()
            .transform((value) => (isNaN(value) ? undefined : value))
            .required(l10n.getString("required-field"))
            .min(1, l10n.getString("required-field")),
        reason: yup.string().required(l10n.getString("required-field")),
        note: yup.string().max(NOTE_MAX_LENGTH),
        itemsForSale: yup
            .number()
            .nullable()
            .transform((value) => (isNaN(value) ? undefined : value))
            .min(0),
    })

    const combinedInitialValues: CombinedStockFormValues = {
        type: initialInventoryValues?.type ?? StockAdjustmentType.INBOUND,
        quantityDelta: initialInventoryValues?.quantityDelta ?? "",
        reason: initialInventoryValues?.reason ?? "",
        note: initialInventoryValues?.note ?? "",
        itemsForSale: currentStock.sales?.daily?.itemsForSale?.toString() ?? "",
    }

    async function handleCombinedSubmit(values: CombinedStockFormValues) {
        const qty = Number(values.quantityDelta)
        const hasInventory = values.quantityDelta && !isNaN(qty) && qty > 0 && values.reason
        const items = Number(values.itemsForSale)
        const hasSales = values.itemsForSale && !isNaN(items)

        if (!hasInventory && !hasSales) return

        const isOutbound = values.type === StockAdjustmentType.OUTBOUND
        if (isOutbound && hasInventory && qty > currentStockTotal) return

        await onStockUpdate({
            productId,
            ...(hasInventory
                ? {
                      inventory: {
                          stockUpdate: {
                              transactionType: ADJUSTMENT_TO_TRANSACTION[values.type],
                              quantityDelta: qty,
                              reason: values.reason as StockChangeReason,
                              note: values.note || undefined,
                          },
                      },
                  }
                : {}),
            ...(hasSales
                ? {
                      sales: {
                          daily: {
                              itemsForSale: items,
                          },
                      },
                  }
                : {}),
        })
        onClose()
    }

    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "top", horizontal: formMode ? "left" : "right" }}
            transformOrigin={{ vertical: "top", horizontal: formMode ? "right" : "left" }}
        >
            <Box className={classes.content}>
                <Formik<CombinedStockFormValues>
                    initialValues={combinedInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={formMode ? () => {} : handleCombinedSubmit}
                    validateOnBlur={false}
                    validateOnChange={false}
                    validateOnMount={showValidationErrors}
                    initialTouched={showValidationErrors ? { reason: true, quantityDelta: true } : undefined}
                >
                    <Form style={{ display: "flex", flexDirection: "column", gap: "3rem" }}>
                        {!formMode && (
                            <Box className={classes.toggleContainer}>
                                <ToggleButtonGroup
                                    value={activeTab}
                                    exclusive
                                    onChange={handleTabChange}
                                    className={classes.toggleGroup}
                                    fullWidth
                                >
                                    <ToggleButton value="inventory" className={classes.toggleButton} disableRipple>
                                        <LocalizedStrict id="product-stock-tab-inventory">
                                            <span>Inventory</span>
                                        </LocalizedStrict>
                                    </ToggleButton>
                                    <ToggleButton value="daily" className={classes.toggleButton} disableRipple>
                                        <LocalizedStrict id="product-stock-tab-daily">
                                            <span>Daily</span>
                                        </LocalizedStrict>
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        )}

                        <Box display={activeTab === "inventory" ? "block" : "none"}>
                            <InventoryFormContent
                                currentStockTotal={currentStockTotal}
                                disableOutbound={disableOutbound}
                                showReasonClear={formMode}
                            />
                        </Box>
                        {!formMode && (
                            <Box display={activeTab === "daily" ? "block" : "none"}>
                                <ItemsPerDayFormContent />
                            </Box>
                        )}

                        {!formMode && <CombinedSubmitButton currentStockTotal={currentStockTotal} onCancel={onClose} />}
                        {formMode && (
                            <FormModeFooter
                                currentStockTotal={currentStockTotal}
                                onCancel={onFormCancel ?? onClose}
                                onConfirm={onValuesChange}
                                onClose={onClose}
                            />
                        )}
                    </Form>
                </Formik>
            </Box>
        </Popover>
    )
})

function CombinedSubmitButton({ currentStockTotal, onCancel }: { currentStockTotal: number; onCancel: () => void }) {
    const { classes } = useStyles()
    const { values, isSubmitting } = useFormikContext<CombinedStockFormValues>()

    const qty = Number(values.quantityDelta)
    const isOutboundExceeded =
        values.type === StockAdjustmentType.OUTBOUND && !isNaN(qty) && qty > 0 && qty > currentStockTotal

    return (
        <Box display="flex" justifyContent="flex-end" gap="1rem">
            <LocalizedStrict id="product-stock-cancel-button">
                <StandardButton
                    fullWidth={false}
                    type="button"
                    size="medium"
                    variant="outlined"
                    onClick={onCancel}
                >
                    Cancel
                </StandardButton>
            </LocalizedStrict>
            <LocalizedStrict id="product-stock-confirm-button">
                <StandardLoadingButton
                    loading={isSubmitting}
                    disabled={isOutboundExceeded}
                    fullWidth={false}
                    type="submit"
                    size="medium"
                    updateDefaultClasses={classes.confirmButton}
                >
                    Confirm
                </StandardLoadingButton>
            </LocalizedStrict>
        </Box>
    )
}

function FormModeFooter({
    currentStockTotal,
    onCancel,
    onConfirm,
    onClose,
}: {
    currentStockTotal: number
    onCancel: () => void
    onConfirm?: (values: CombinedStockFormValues) => void
    onClose: () => void
}) {
    const { classes } = useStyles()
    const { values } = useFormikContext<CombinedStockFormValues>()

    const qty = Number(values.quantityDelta)
    const isOutboundExceeded =
        values.type === StockAdjustmentType.OUTBOUND && !isNaN(qty) && qty > 0 && qty > currentStockTotal

    const handleConfirm = () => {
        onConfirm?.(values)
        onClose()
    }

    return (
        <Box display="flex" justifyContent="flex-end" gap="1rem">
            <LocalizedStrict id="product-stock-cancel-button">
                <StandardButton fullWidth={false} type="button" size="medium" variant="outlined" onClick={onCancel}>
                    Cancel
                </StandardButton>
            </LocalizedStrict>
            <LocalizedStrict id="product-stock-confirm-button">
                <StandardButton
                    fullWidth={false}
                    type="button"
                    size="medium"
                    disabled={isOutboundExceeded}
                    updateDefaultClasses={classes.confirmButton}
                    onClick={handleConfirm}
                >
                    Confirm
                </StandardButton>
            </LocalizedStrict>
        </Box>
    )
}

const NOTE_MAX_LENGTH = 200

function InventoryFormContent({
    currentStockTotal,
    disableOutbound,
    showReasonClear,
}: {
    currentStockTotal?: number
    disableOutbound?: boolean
    showReasonClear?: boolean
}) {
    const { classes } = useStyles()
    const { values, setFieldValue } = useFormikContext<CombinedStockFormValues>()

    const adjustmentTypes = disableOutbound
        ? [StockAdjustmentType.INBOUND]
        : [...iterateStringEnum(StockAdjustmentType)]
    const reasons = values.type === StockAdjustmentType.INBOUND ? INBOUND_REASONS : OUTBOUND_REASONS

    const qty = Number(values.quantityDelta)
    const isOutboundExceeded =
        currentStockTotal != null &&
        values.type === StockAdjustmentType.OUTBOUND &&
        !isNaN(qty) &&
        qty > 0 &&
        qty > currentStockTotal

    const handleTypeChange = () => {
        setFieldValue("reason", "")
    }

    return (
        <Box display="flex" flexDirection="column" gap="1.5rem">
            {currentStockTotal != null && (
                <Box display="flex" alignItems="center" gap="0.5rem">
                    <LocalizedStrict id="product-stock-current-stock-label">
                        <Typography variant="body1" fontWeight={500}>
                            Current stock:
                        </Typography>
                    </LocalizedStrict>
                    <Typography variant="body1" fontWeight={400}>
                        {currentStockTotal}
                    </Typography>
                </Box>
            )}

            <Box className={classes.fieldGroup}>
                <Box className={classes.labelRow}>
                    <LocalizedStrict id="product-stock-type-label">
                        <Typography variant="body1" color="textPrimary" fontWeight={500}>
                            Type:
                        </Typography>
                    </LocalizedStrict>
                </Box>
                <FormikDropdown name="type" fullWidth className={classes.reasonField} onChange={handleTypeChange}>
                    {adjustmentTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                            <LocalizedEnum base="product-stock-type-option" enumValue={type}>
                                <span>{type}</span>
                            </LocalizedEnum>
                        </MenuItem>
                    ))}
                </FormikDropdown>
            </Box>

            <Box className={classes.fieldGroup}>
                <Box className={classes.labelRow}>
                    <LocalizedStrict id="product-stock-adjustment-amount-label">
                        <Typography variant="body1" color="textPrimary" fontWeight={500}>
                            Quantity:
                        </Typography>
                    </LocalizedStrict>
                    <LocalizedStrict id="product-stock-adjustment-amount-tooltip" attrs={{ title: true }}>
                        <Tooltip title="Enter the quantity to adjust">
                            <FontAwesomeIcon icon={faCircleInfo} className={classes.infoIcon} />
                        </Tooltip>
                    </LocalizedStrict>
                </Box>
                <FormikNumberField
                    name="quantityDelta"
                    integerOnly
                    maxLength={6}
                    className={classes.inputField}
                />
                {isOutboundExceeded && (
                    <LocalizedStrict id="product-stock-negative-error">
                        <Typography variant="caption" color="error">
                            Stock cannot be negative
                        </Typography>
                    </LocalizedStrict>
                )}
            </Box>

            <Box className={classes.fieldGroup}>
                <Box className={classes.labelRow}>
                    <LocalizedStrict id="product-stock-reason-label">
                        <Typography variant="body1" color="textPrimary" fontWeight={500}>
                            Reason:
                        </Typography>
                    </LocalizedStrict>
                    <LocalizedStrict id="product-stock-reason-tooltip" attrs={{ title: true }}>
                        <Tooltip title="Select the reason for this stock adjustment">
                            <FontAwesomeIcon icon={faCircleInfo} className={classes.infoIcon} />
                        </Tooltip>
                    </LocalizedStrict>
                </Box>
                <FormikDropdown
                    name="reason"
                    fullWidth
                    className={classes.reasonField}
                    endAdornment={
                        showReasonClear && values.reason ? (
                            <IconButton
                                className={classes.clearButton}
                                onClick={() => setFieldValue("reason", "")}
                                size="small"
                            >
                                <FontAwesomeIcon icon={faClose} />
                            </IconButton>
                        ) : undefined
                    }
                >
                    {reasons.map((reason) => (
                        <MenuItem key={reason} value={reason}>
                            <LocalizedEnum
                                base="product-stock-reason-tooltip"
                                enumValue={reason}
                                attrs={{ title: true }}
                            >
                                <Tooltip title="reason tooltip" placement="right">
                                    <Box width="100%">
                                        <LocalizedEnum base="product-stock-reason-option" enumValue={reason}>
                                            <span>{reason}</span>
                                        </LocalizedEnum>
                                    </Box>
                                </Tooltip>
                            </LocalizedEnum>
                        </MenuItem>
                    ))}
                </FormikDropdown>
            </Box>

            <Box className={classes.fieldGroup}>
                <Box className={classes.labelRow}>
                    <LocalizedStrict id="product-stock-note-label">
                        <Typography variant="body1" color="textPrimary" fontWeight={500}>
                            Note:
                        </Typography>
                    </LocalizedStrict>
                </Box>
                <LocalizedStrict id="product-stock-note-placeholder" attrs={{ placeholder: true }}>
                    <FormikTextArea
                        name="note"
                        fullWidth
                        expandable
                        maxRows={3}
                        maxLength={NOTE_MAX_LENGTH}
                        placeholder="Optional extra info about adjustment"
                        className={classes.noteField}
                    />
                </LocalizedStrict>
            </Box>
        </Box>
    )
}

function ItemsPerDayFormContent() {
    const { classes } = useStyles()

    return (
        <Box display="flex" flexDirection="column" gap="1.5rem">
            <Box className={classes.fieldGroup}>
                <Box className={classes.labelRow}>
                    <LocalizedStrict id="product-stock-items-per-day-label">
                        <Typography variant="body1" color="textPrimary" fontWeight={500}>
                            Items per day
                        </Typography>
                    </LocalizedStrict>
                    <LocalizedStrict id="product-stock-on-sale-per-day-info" attrs={{ title: true }}>
                        <Tooltip title="Using this you can limit the number of items on sale each day.">
                            <FontAwesomeIcon icon={faCircleInfo} className={classes.infoIcon} />
                        </Tooltip>
                    </LocalizedStrict>
                </Box>
                <FormikNumberField
                    name="itemsForSale"
                    integerOnly
                    maxLength={6}
                    min={0}
                    className={classes.inputFieldSmall}
                />
            </Box>
        </Box>
    )
}
